import { FormEvent, useMemo } from 'react';
import { Info, ListPlus, Loader2, Save, X } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import QuickAddProduct from '../../../components/QuickAdd/Product/QuickAddProduct';
import MenuCategoryQuickField from './MenuCategoryQuickField';
import StationQuickField from './StationQuickField';

import {
  MenuProductOption,
  RestaurantMenuCategoryDTO,
  RestaurantMenuItemForm,
} from '../../../types/restaurant/RestaurantMenuTypes';
import { RestaurantPreparationStationDTO } from '../../../types/restaurant/RestaurantPreparationStationTypes';

interface Props {
  isOpen: boolean;
  form: RestaurantMenuItemForm;
  categories: RestaurantMenuCategoryDTO[];
  products: MenuProductOption[];
  stations: RestaurantPreparationStationDTO[];
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantMenuItemForm>(
    field: K,
    value: RestaurantMenuItemForm[K]
  ) => void;
  onCreateCategory: (
    name: string
  ) => Promise<RestaurantMenuCategoryDTO | null>;
  onCreateStation: (
    name: string
  ) => Promise<RestaurantPreparationStationDTO | null>;
  onReloadProducts: () => void | Promise<void>;
  /** La sucursal cierra las comandas por tiempo: el tiempo de preparación es obligatorio. */
  requirePreparationTime?: boolean;
  /** Abre el gestor de grupos de modificadores del ítem (solo al editar). */
  onManageModifiers?: () => void;
  onSave: () => void;
}

const MenuItemFormModal = ({
  isOpen,
  form,
  categories,
  products,
  stations,
  saving,
  onClose,
  onChange,
  onCreateCategory,
  onCreateStation,
  onReloadProducts,
  requirePreparationTime = false,
  onManageModifiers,
  onSave,
}: Props) => {
  // =====================================================
  // 🔹 Opciones
  // =====================================================

  const productOptions = useMemo(() => {
    const options = products.map((product) => ({
      id: product.id,
      name: `${product.name} · ${product.category || 'Sin categoría'}`,
    }));

    // Al editar: si el producto vinculado no está en la lista del dropdown
    // (lista desactualizada), agrégalo para que el selector no se vea vacío.
    const currentId = form.productId ? Number(form.productId) : null;
    if (currentId && !options.some((option) => option.id === currentId)) {
      options.unshift({
        id: currentId,
        name: form.productCode
          ? `${form.productCode} · ${form.productName ?? 'Producto vinculado'}`
          : form.productName ?? 'Producto vinculado',
      });
    }

    return options;
  }, [products, form.productId, form.productName, form.productCode]);

  // =====================================================
  // 🔹 Validaciones
  // =====================================================

  const errors = useMemo(() => {
    const validationErrors: Record<string, string> = {};

    if (!form.productId) {
      validationErrors.productId = 'Seleccione un producto.';
    }

    if (!form.categoryId) {
      validationErrors.categoryId = 'Seleccione una categoría.';
    }

    if (form.preparationTimeMinutes !== '') {
      const preparationTime = Number(form.preparationTimeMinutes);

      if (
        Number.isNaN(preparationTime) ||
        preparationTime < 0 ||
        preparationTime > 1440
      ) {
        validationErrors.preparationTimeMinutes =
          'Debe estar entre 0 y 1440 minutos.';
      } else if (requirePreparationTime && preparationTime <= 0) {
        validationErrors.preparationTimeMinutes =
          'La cocina cierra las comandas por tiempo: indica cuántos minutos tarda.';
      }
    } else if (requirePreparationTime) {
      validationErrors.preparationTimeMinutes =
        'La cocina cierra las comandas por tiempo: indica cuántos minutos tarda.';
    }

    if (form.displayName && form.displayName.length > 150) {
      validationErrors.displayName =
        'El nombre visible debe tener máximo 150 caracteres.';
    }

    if (form.description && form.description.length > 500) {
      validationErrors.description =
        'La descripción debe tener máximo 500 caracteres.';
    }

    return validationErrors;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  // =====================================================
  // 🔹 Eventos
  // =====================================================

  const handleSave = () => {
    if (saving || hasErrors) {
      return;
    }

    onSave();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSave();
  };

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : handleClose}
      title={form.id ? 'Editar ítem del menú' : 'Agregar ítem al menú'}
      maxWidth="max-w-3xl"
      headerExtra={<RestaurantFormHelp topic="menuItem" />}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Información */}
        <div
          className="
            flex items-start gap-3
            rounded-xl border
            border-primary/15
            bg-primary/5
            p-4 text-sm
            text-black
            dark:text-white
          "
        >
          <span
            className="
              flex h-9 w-9 shrink-0
              items-center justify-center
              rounded-lg bg-primary/10
              text-primary
            "
          >
            <Info className="h-5 w-5" aria-hidden="true" />
          </span>

          <div>
            <p className="font-medium">Precio e imagen vinculados al producto</p>

            <p className="mt-1 text-body dark:text-bodydark2">
              El precio y la imagen no se registran en el menú: siempre se usan
              los del producto seleccionado. El código del ítem se genera
              automáticamente a partir de la categoría (ej. BEB-001).
            </p>
          </div>
        </div>

        <QuickAddProduct onReload={onReloadProducts} />

        {/* Campos principales */}
        <div
          className="
            grid grid-cols-1 gap-x-5
            md:grid-cols-2
          "
        >
          <SimpleSearchSelect
            label="Producto"
            name="productId"
            placeholder="Seleccione un producto"
            searchPlaceholder="Buscar producto..."
            selectedValue={form.productId ? Number(form.productId) : null}
            options={productOptions}
            iconKey="product"
            required
            disabled={saving}
            error={errors.productId}
            onSelect={(selectedProduct) =>
              onChange('productId', selectedProduct.id)
            }
          />

          <MenuCategoryQuickField
            categories={categories}
            value={form.categoryId}
            error={errors.categoryId}
            disabled={saving}
            onChange={(id) => onChange('categoryId', id)}
            onCreateCategory={onCreateCategory}
          />

          {form.id && (
            <InputField
              label="Código del ítem"
              name="code"
              type="text"
              iconKey="code"
              value={form.code ?? ''}
              disabled
              onChange={() => undefined}
            />
          )}

          <InputField
            label="Nombre visible"
            name="displayName"
            type="text"
            iconKey="tag"
            value={form.displayName}
            disabled={saving}
            error={errors.displayName}
            placeholder="Usa el nombre del producto si queda vacío"
            onChange={(event) => onChange('displayName', event.target.value)}
          />

          <InputField
            label={
              requirePreparationTime
                ? 'Preparación (minutos) *'
                : 'Preparación (minutos)'
            }
            name="preparationTimeMinutes"
            type="number"
            iconKey="clock"
            value={form.preparationTimeMinutes}
            min={0}
            max={1440}
            disabled={saving}
            error={errors.preparationTimeMinutes}
            placeholder="Ej. 15"
            onChange={(event) =>
              onChange(
                'preparationTimeMinutes',
                event.target.value !== '' ? Number(event.target.value) : ''
              )
            }
          />

          <StationQuickField
            stations={stations}
            value={form.preparationStationId}
            disabled={saving}
            onChange={(id) => onChange('preparationStationId', id)}
            onCreateStation={onCreateStation}
          />

          <InputField
            label="Orden de visualización"
            name="displayOrder"
            type="number"
            iconKey="list"
            value={form.displayOrder}
            min={0}
            disabled={saving}
            placeholder="Ej. 1"
            onChange={(event) =>
              onChange('displayOrder', Math.max(0, Number(event.target.value)))
            }
          />
        </div>

        {/* Configuración */}
        <div>
          <p
            className="
              mb-2.5 text-sm font-medium
              text-black dark:text-white
            "
          >
            Configuración del ítem
          </p>

          <div
            className="
              bg-gray-1 grid grid-cols-1
              gap-4 rounded-xl border
              border-stroke p-4
              dark:border-strokedark
              dark:bg-meta-4
              sm:grid-cols-2
            "
          >
            <ToggleSwitch
              label="Disponible para venta"
              name="menuItemAvailable"
              checked={form.available}
              disabled={saving}
              onChange={() => onChange('available', !form.available)}
            />

            <ToggleSwitch
              label="Destacado"
              name="menuItemFeatured"
              checked={form.featured}
              disabled={saving}
              onChange={() => onChange('featured', !form.featured)}
            />

            {form.id && (
              <div className="sm:col-span-2">
                <div
                  className="
                    border-t border-stroke pt-4
                    dark:border-strokedark
                  "
                >
                  <ToggleSwitch
                    label="Ítem activo"
                    name="menuItemActive"
                    checked={form.active ?? true}
                    disabled={saving}
                    onChange={() => onChange('active', !(form.active ?? true))}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Descripción */}
        <TextAreaField
          label="Descripción"
          name="description"
          value={form.description ?? ''}
          rows={4}
          disabled={saving}
          error={errors.description}
          placeholder="Describe el producto dentro del menú..."
          helperText="Esta descripción puede mostrarse al cliente dentro del menú."
          rightContent={
            <span
              className={
                form.description.length >= 500
                  ? 'text-danger'
                  : 'text-body dark:text-bodydark2'
              }
            >
              {form.description.length}/500
            </span>
          }
          onChange={(event) =>
            onChange('description', event.target.value.slice(0, 500))
          }
        />

        {/* Acciones */}
        <div
          className="
            flex flex-col-reverse gap-3
            border-t border-stroke pt-5
            dark:border-strokedark
            sm:flex-row sm:justify-end
          "
        >
          {form.id && onManageModifiers && (
            <ActionButton
              label="Modificadores"
              variant="outline"
              size="md"
              fullWidth={false}
              disabled={saving}
              icon={<ListPlus className="h-4 w-4" aria-hidden="true" />}
              onClick={onManageModifiers}
              className="w-full sm:mr-auto sm:w-auto"
            />
          )}

          <ActionButton
            label="Cancelar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            icon={<X className="h-4 w-4" aria-hidden="true" />}
            onClick={handleClose}
            className="w-full sm:w-auto"
          />

          <ActionButton
            label={
              saving
                ? 'Guardando...'
                : form.id
                ? 'Guardar cambios'
                : 'Guardar ítem'
            }
            variant="primary"
            size="md"
            fullWidth={false}
            disabled={saving || hasErrors}
            icon={
              saving ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-4 w-4" aria-hidden="true" />
              )
            }
            onClick={handleSave}
            className="w-full sm:w-auto sm:min-w-[165px]"
          />
        </div>
      </form>
    </FormModal>
  );
};

export default MenuItemFormModal;
